import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Hospital, ShieldCheck, ArrowLeft, HeartPulse, Sparkles, Ambulance } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { authApi, customerApi, consentApi, setDeviceToken, referralApi } from "@/lib/api";
import { trackFunnel, setAnalyticsUser } from "@/lib/analytics";
import { useAuthStore } from "@/store/authStore";
import { useLanguageStore } from "@/store/languageStore";
import logoWide from "@/assets/trata-health-logo-wide.png";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];

function normalizeProfile(cp) {
  if (!cp) return null;
  return cp;
}

function LanguageToggle({ language, setLanguage }) {
  return (
    <div className="flex items-center gap-1 rounded-full bg-muted p-1" data-testid="lang-toggle">
      {[
        { code: "en", label: "EN" },
        { code: "hi", label: "हिं" },
      ].map((l) => (
        <button
          key={l.code}
          data-testid={`lang-toggle-${l.code}`}
          onClick={() => setLanguage(l.code)}
          className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors ${
            language === l.code ? "bg-primary text-primary-foreground" : "text-muted-foreground"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Auth() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setSession = useAuthStore((s) => s.setSession);
  const language = useLanguageStore((s) => s.language);
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const isLanguageSelected = useLanguageStore((s) => s.isLanguageSelected);

  const [step, setStep] = useState("phone"); // phone | otp | pin | setPin | signup
  const [loading, setLoading] = useState(false);

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [keepLoggedIn, setKeepLoggedIn] = useState(true);
  const [pendingUser, setPendingUser] = useState(null);

  // PIN state
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinAttemptsLeft, setPinAttemptsLeft] = useState(null);
  const [pinForReset, setPinForReset] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null);

  // signup fields
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [city, setCity] = useState("");
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);

  // Hospital referral (from a partner's QR / link, or typed manually)
  const [referralCode, setReferralCode] = useState("");
  const [referralHospital, setReferralHospital] = useState(null);
  const [referralChannel, setReferralChannel] = useState("manual_code");
  const [resolvingRef, setResolvingRef] = useState(false);
  const [refError, setRefError] = useState("");

  const cleanPhone = phone.replace(/\D/g, "");

  const resolveReferral = async (raw, channel) => {
    const c = (raw || "").trim().toUpperCase();
    setReferralHospital(null);
    setRefError("");
    if (!c) return;
    setResolvingRef(true);
    try {
      const res = await referralApi.resolve(c);
      setReferralHospital(res.data?.hospital || null);
      if (channel) setReferralChannel(channel);
    } catch (e) {
      setRefError("We couldn't find a hospital for this code.");
    } finally {
      setResolvingRef(false);
    }
  };

  // Pick up a referral code passed via /r/:code -> /auth?ref=CODE, or a value stashed on this browser.
  useEffect(() => {
    let code = "";
    let channel = "manual_code";
    try {
      const qp = new URLSearchParams(window.location.search).get("ref");
      if (qp) {
        code = qp;
        channel = "referral_link";
      } else {
        const raw = localStorage.getItem("trata_pending_referral");
        if (raw) code = (JSON.parse(raw)?.code || "");
      }
    } catch (e) {
      // ignore
    }
    if (code) {
      setReferralCode(code.toUpperCase());
      setReferralChannel(channel);
      resolveReferral(code, channel);
    }
  }, []);


  // First-time users pick a language before logging in (mirrors the mobile app).
  useEffect(() => {
    if (!isLanguageSelected) navigate("/language-select", { replace: true });
  }, [isLanguageSelected, navigate]);

  // ---- Auth finalize (shared by OTP, PIN and Set-PIN flows) ----
  const finalizeAuth = (responseData) => {
    const user = responseData?.user;
    const cp = normalizeProfile(responseData?.customerProfile);
    if (!user) throw new Error("no user");
    const hasProfile = !!(cp && (cp.basicInfo?.fullName || cp.fullName));
    if (hasProfile) {
      setSession(user, cp, keepLoggedIn);
      try {
        setAnalyticsUser({ userId: user._id || user.id, phone: cleanPhone });
        trackFunnel("signup_complete");
      } catch (e) {}
      // Master admin gets the Admin Portal / Patient App chooser after login.
      if (cleanPhone === "9873117605") {
        navigate("/admin-choice", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } else {
      setPendingUser(user);
      setStep("signup");
    }
  };

  const registerTrustedDevice = async () => {
    try {
      const res = await authApi.registerTrustedDevice(cleanPhone);
      if (res.data?.device_token) setDeviceToken(cleanPhone, res.data.device_token);
    } catch (e) {
      // non-blocking — PIN just won't be offered next time
    }
  };

  const doSendOtp = async () => {
    const res = await authApi.sendOtp(cleanPhone);
    try { trackFunnel("otp_sent"); } catch (e) {}
    setStep("otp");
    setOtp("");
    toast.success(res?.data?.message || "OTP sent to your phone number");
  };

  // Phone entry → decide PIN vs OTP path (no OTP sent for trusted PIN users)
  const handlePhoneContinue = async () => {
    if (cleanPhone.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    try { trackFunnel("phone_entered"); } catch (e) {}
    setLoading(true);
    try {
      // check-user is a nice-to-have (PIN fast-path); never let it block OTP.
      let data = null;      try {
        const r = await authApi.checkUser(cleanPhone);
        data = r.data;
      } catch (checkErr) {
        // ignore — proceed to send OTP
      }
      if (data && data.exists && data.hasPin && data.deviceTrusted && !data.pinLocked) {
        setPin("");
        setPinError("");
        setPinAttemptsLeft(null);
        setStep("pin");
        setLoading(false);
        return;
      }
      if (data && data.exists && data.hasPin && data.pinLocked) setPinForReset(true);
      await doSendOtp();
    } catch (e) {
      // Surface the real reason so deployment/network issues are diagnosable.
      if (!e?.response) {
        toast.error("Can't reach the server. Please check your internet connection and try again.");
      } else {
        toast.error(e.response.data?.detail || "Could not send OTP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code) => {
    // Strip any non-digits (SMS autofill / paste can add spaces or invisible chars)
    const val = ((code || otp) + "").replace(/\D/g, "").slice(0, 6);
    if (val.length !== 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.verifyOtp(cleanPhone, val);
      const responseData = res.data;
      if (!responseData?.user) throw new Error("no user");
      try { trackFunnel("otp_verified"); } catch (e) {}

      // This browser becomes a trusted device → PIN login enabled next time
      await registerTrustedDevice();

      // Mandatory 4-digit PIN: set it if user has none, or when resetting.
      if (!responseData.hasPin || pinForReset) {
        setPendingAuth(responseData);
        setPin("");
        setConfirmPin("");
        setPinError("");
        setPinForReset(false);
        setStep("setPin");
        setLoading(false);
        return;
      }

      finalizeAuth(responseData);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired OTP");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async (pinOverride) => {
    const val = pinOverride ?? pin;
    if (val.length !== 4) {
      setPinError(t("pinUI.pin4required"));
      return;
    }
    setLoading(true);
    setPinError("");
    try {
      const { data } = await authApi.verifyPin(cleanPhone, val);
      if (data.success) {
        finalizeAuth(data);
        return;
      }
      // Wrong / locked
      if (data.requiresOtp || data.locked) {
        setPinError(t("pinUI.tooManyAttempts"));
        setPinForReset(true);
        setPin("");
        await doSendOtp();
        setLoading(false);
        return;
      }
      setPinAttemptsLeft(typeof data.attemptsLeft === "number" ? data.attemptsLeft : null);
      setPinError(data.message || t("pinUI.incorrect"));
      setPin("");
      setLoading(false);
    } catch (e) {
      const status = e?.response?.status;
      setPin("");
      setLoading(false);
      if (status === 403) {
        // Device not trusted or no PIN → OTP fallback
        setPinForReset(true);
        try {
          await doSendOtp();
        } catch (err) {
          setPinError(t("pinUI.incorrect"));
        }
      } else {
        setPinError(e?.response?.data?.detail || t("pinUI.incorrect"));
      }
    }
  };

  const handleSetPin = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setPinError(t("pinUI.pin4required"));
      return;
    }
    if (pin !== confirmPin) {
      setPinError(t("pinUI.pinMismatch"));
      return;
    }
    setLoading(true);
    setPinError("");
    try {
      await authApi.setPin(cleanPhone, pin);
      const resp = pendingAuth;
      setPendingAuth(null);
      setPin("");
      setConfirmPin("");
      if (resp) {
        finalizeAuth(resp);
      } else {
        setStep("phone");
      }
    } catch (e) {
      setPinError(e?.response?.data?.detail || "Could not set PIN");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPin = async () => {
    setPinForReset(true);
    setPin("");
    setPinError("");
    setLoading(true);
    try {
      await doSendOtp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!fullName.trim()) return toast.error("Please enter your full name");
    if (!dob.match(/^\d{2}\/\d{2}\/\d{4}$/)) return toast.error("Date of birth must be DD/MM/YYYY");
    if (!gender) return toast.error("Please select your gender");
    if (!bloodGroup) return toast.error("Please select your blood group");
    if (!terms || !privacy) return toast.error("Please accept the Terms and Privacy Policy");
    setLoading(true);
    try {
      try { trackFunnel("profile_submitted"); } catch (e) {}
      const res = await customerApi.createProfile({
        phone: cleanPhone,
        userId: pendingUser?._id,
        fullName: fullName.trim(),
        dateOfBirth: dob,
        gender,
        bloodGroup,
        city: city.trim() || undefined,
      });
      const profile = res.data?.profile;
      try {
        await consentApi.recordBulk({
          phone: cleanPhone,
          userId: pendingUser?._id,
          consentVersion: "2.0",
          consents: [
            { consentType: "terms_of_service", consented: true },
            { consentType: "privacy_policy", consented: true },
          ],
        });
      } catch (ce) {
        // non-blocking — consent can be re-recorded later
      }
      const mergedProfile = profile
        ? { ...profile, basicInfo: profile.basic_info || profile.basicInfo, patientId: profile.patient_id || profile.patientId }
        : null;
      // Link the referring hospital (from QR / link / manual code) — best-effort.
      if (referralHospital && pendingUser?._id) {
        try {
          await referralApi.savePreferred(pendingUser._id, {
            referralCode: referralCode.trim().toUpperCase(),
            source: "referral",
            channel: referralChannel,
          });
          localStorage.removeItem("trata_pending_referral");
        } catch (re) {
          // non-blocking
        }
      }
      setSession({ ...pendingUser, name: fullName.trim(), role: "customer" }, mergedProfile, keepLoggedIn);
      toast.success("Welcome to Trata Health!");
      navigate("/home", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create your profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand panel (desktop) */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-primary text-primary-foreground p-12">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-primary-foreground/15 flex items-center justify-center">
            <Hospital className="h-6 w-6" />
          </div>
          <span className="text-xl font-semibold">Trata Health</span>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl font-semibold leading-tight">
            Your family health, all in one calm place.
          </h1>
          <p className="mt-4 text-primary-foreground/80 text-lg">
            Find hospitals & doctors, book consultations, keep your reports safe, and ask
            Dr Sanjeevani anything — anytime.
          </p>
          <div className="mt-10 space-y-4">
            {[
              { icon: HeartPulse, t: "Book doctors & hospital services" },
              { icon: Sparkles, t: "AI health assistant, Dr Sanjeevani" },
              { icon: Ambulance, t: "Emergency & care coordination" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <f.icon className="h-5 w-5" />
                <span className="text-primary-foreground/90">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-sm text-primary-foreground/60">DPDP-compliant • India-hosted</p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 bg-background relative">
        {/* Language toggle (top-right) */}
        <div className="absolute top-5 right-5">
          <LanguageToggle language={language} setLanguage={setLanguage} />
        </div>

        <div className="w-full max-w-sm">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div className="h-32 w-32 rounded-2xl bg-card border border-border flex items-center justify-center p-3 shadow-sm">
              <img src={logoWide} alt="Trata Health" className="h-full w-auto object-contain" />
            </div>
            <span className="mt-2 text-xs font-semibold text-primary bg-secondary px-3 py-1 rounded-full">
              {t("loginUI.patientsOnly")}
            </span>
          </div>

          {step === "phone" && (
            <div className="animate-fade-in-up">
              <h2 className="text-2xl font-semibold">{t("loginUI.welcomeHeading")}</h2>
              <p className="text-muted-foreground mt-1">{t("loginUI.loginOrSignup")}</p>
              <div className="mt-8">
                <Label htmlFor="phone">{t("loginUI.phoneNumber")}</Label>
                <div className="mt-2 flex items-center gap-2">
                  <span className="h-12 px-3 flex items-center rounded-2xl bg-muted text-sm font-medium text-foreground">+91</span>
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    inputMode="numeric"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && handlePhoneContinue()}
                    placeholder={t("loginUI.enterTenDigitNumber")}
                    className="flex-1 h-12 rounded-2xl bg-muted border-0 focus-visible:ring-1 focus-visible:ring-ring text-base"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Checkbox
                  id="keep-logged-in"
                  data-testid="keep-logged-in-checkbox"
                  checked={keepLoggedIn}
                  onCheckedChange={(v) => setKeepLoggedIn(v === true)}
                />
                <Label htmlFor="keep-logged-in" className="text-sm font-normal text-muted-foreground cursor-pointer">
                  {t("loginUI.keepLoggedIn")}
                </Label>
              </div>
              <Button
                className="w-full mt-6 h-12 rounded-2xl text-base"
                onClick={handlePhoneContinue}
                disabled={loading}
                data-testid="send-otp-button"
              >
                {loading ? t("common.sending") : t("common.continue")}
              </Button>

              {/* Guest Emergency SOS (matches mobile) */}
              <a
                href="tel:108"
                data-testid="guest-sos"
                className="mt-4 flex items-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3"
              >
                <div className="h-9 w-9 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <Ambulance className="h-5 w-5 text-destructive" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-destructive">{t("emergencyUI.sos")}</p>
                  <p className="text-xs text-muted-foreground">{t("emergencyUI.callAmbulance")}</p>
                </div>
                <ArrowLeft className="h-4 w-4 text-muted-foreground rotate-180" />
              </a>
              <div className="mt-5 flex justify-center">
                <span className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--tab-home))] bg-[hsl(var(--tab-home)/0.1)] px-3 py-1 rounded-full">
                  <ShieldCheck className="h-3 w-3" /> {t("loginUI.dpdpCompliant")}
                </span>
              </div>
            </div>
          )}

          {step === "otp" && (
            <div className="animate-fade-in-up">
              <button
                className="flex items-center gap-1 text-sm text-muted-foreground mb-4"
                onClick={() => setStep("phone")}
                data-testid="otp-back"
              >
                <ArrowLeft className="h-4 w-4" /> {t("common.back")}
              </button>
              <h2 className="text-2xl font-semibold">{t("loginUI.verifyNumberTitle")}</h2>
              <p className="text-muted-foreground mt-1">
                {t("loginUI.otpCodeSubtitle", { phone: cleanPhone })}
              </p>
              <div className="mt-8 flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} data-testid={`otp-cell-${i}`} className="h-12 w-11 text-lg rounded-xl" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                className="w-full mt-8 h-12 rounded-2xl text-base"
                onClick={() => handleVerifyOtp()}
                disabled={loading}
                data-testid="verify-otp-button"
              >
                {loading ? t("loginUI.verifying") : t("loginUI.verifyContinue")}
              </Button>
              <button
                className="w-full text-center text-sm text-primary mt-4"
                onClick={handlePhoneContinue}
                data-testid="resend-otp"
              >
                {t("loginUI.resendCode")}
              </button>
            </div>
          )}

          {step === "pin" && (
            <div className="animate-fade-in-up">
              <button
                className="flex items-center gap-1 text-sm text-muted-foreground mb-4"
                onClick={() => {
                  setStep("phone");
                  setPin("");
                  setPinError("");
                }}
                data-testid="pin-back"
              >
                <ArrowLeft className="h-4 w-4" /> {t("common.back")}
              </button>
              <h2 className="text-2xl font-semibold">{t("pinUI.enterPinTitle")}</h2>
              <p className="text-muted-foreground mt-1">{t("pinUI.enterPinSubtitle")}</p>
              <div className="mt-8">
                <Label>{t("pinUI.enter4digit")}</Label>
                <div className="mt-3 flex justify-center">
                  <InputOTP
                    maxLength={4}
                    value={pin}
                    onChange={(v) => {
                      const clean = v.replace(/\D/g, "").slice(0, 4);
                      setPin(clean);
                      setPinError("");
                      if (clean.length === 4 && !loading) handleVerifyPin(clean);
                    }}
                    data-testid="pin-input"
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3].map((i) => (
                        <InputOTPSlot key={i} index={i} className="h-14 w-12 text-xl rounded-xl" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {!!pinError && (
                  <p className="mt-3 text-sm text-destructive text-center" data-testid="pin-error">
                    {pinError}
                  </p>
                )}
                {pinAttemptsLeft !== null && pinAttemptsLeft > 0 && !pinError && (
                  <p className="mt-3 text-sm text-muted-foreground text-center">
                    {t("pinUI.attemptsLeft", { count: pinAttemptsLeft })}
                  </p>
                )}
              </div>
              <div className="mt-6 flex items-center justify-between text-sm">
                <button
                  className="text-muted-foreground"
                  onClick={() => {
                    setStep("phone");
                    setPin("");
                    setPinError("");
                  }}
                  data-testid="pin-change-number"
                >
                  {t("pinUI.changeNumber")}
                </button>
                <button className="text-primary font-medium" onClick={handleForgotPin} disabled={loading} data-testid="forgot-pin">
                  {t("pinUI.forgotPin")}
                </button>
              </div>
            </div>
          )}

          {step === "setPin" && (
            <div className="animate-fade-in-up">
              <h2 className="text-2xl font-semibold">{t("pinUI.setPinTitle")}</h2>
              <p className="text-muted-foreground mt-1">{t("pinUI.setPinSubtitle")}</p>
              <div className="mt-8 space-y-5">
                <div>
                  <Label>{t("pinUI.create4digit")}</Label>
                  <p className="text-xs text-muted-foreground mt-1">{t("pinUI.pinHint")}</p>
                  <div className="mt-3 flex justify-center">
                    <InputOTP
                      maxLength={4}
                      value={pin}
                      onChange={(v) => {
                        setPin(v.replace(/\D/g, "").slice(0, 4));
                        setPinError("");
                      }}
                      data-testid="set-pin-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-14 w-12 text-xl rounded-xl" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <div>
                  <Label>{t("pinUI.confirmPin")}</Label>
                  <div className="mt-3 flex justify-center">
                    <InputOTP
                      maxLength={4}
                      value={confirmPin}
                      onChange={(v) => {
                        setConfirmPin(v.replace(/\D/g, "").slice(0, 4));
                        setPinError("");
                      }}
                      data-testid="confirm-pin-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-14 w-12 text-xl rounded-xl" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                {!!pinError && (
                  <p className="text-sm text-destructive text-center" data-testid="set-pin-error">
                    {pinError}
                  </p>
                )}
              </div>
              <Button
                className="w-full mt-8 h-12 rounded-2xl text-base"
                onClick={handleSetPin}
                disabled={loading}
                data-testid="set-pin-button"
              >
                {loading ? t("pinUI.verifying") : t("pinUI.setPinButton")}
              </Button>
            </div>
          )}

          {step === "signup" && (
            <div className="animate-fade-in-up">
              <h2 className="text-2xl font-semibold">{t("signupUI.title")}</h2>
              <p className="text-muted-foreground mt-1">{t("signupUI.subtitle")}</p>
              <div className="mt-6 space-y-4">
                {/* Hospital referral code (optional) */}
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                  <Label htmlFor="referral" className="flex items-center gap-1.5 text-primary font-semibold">
                    <Hospital className="h-4 w-4" /> Hospital Referral Code (optional)
                  </Label>
                  <Input
                    id="referral"
                    data-testid="signup-referral"
                    value={referralCode}
                    onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralChannel("manual_code"); setReferralHospital(null); setRefError(""); }}
                    onBlur={() => resolveReferral(referralCode, "manual_code")}
                    className="mt-2 rounded-2xl h-11 bg-card"
                    placeholder="Enter the referral code from your hospital"
                  />
                  {resolvingRef && <p className="text-xs text-muted-foreground mt-2">Checking code…</p>}
                  {refError && <p className="text-xs text-destructive mt-2" data-testid="referral-error">{refError}</p>}
                  {referralHospital && (
                    <div className="mt-3 flex items-center gap-2 rounded-xl bg-card border border-primary/30 p-3" data-testid="referral-confirm">
                      <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
                      <p className="text-sm">
                        Referred by <span className="font-semibold">{referralHospital.name}</span>
                        {referralHospital.city ? `, ${referralHospital.city}` : ""}. We&apos;ll link it to your care.
                      </p>
                    </div>
                  )}
                  {!referralHospital && !resolvingRef && !refError && (
                    <p className="text-xs text-muted-foreground mt-2">Referred by a hospital? Enter their code to link your care. You can skip this.</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="name">{t("signupUI.fullNameLabel")}</Label>
                  <Input
                    id="name"
                    data-testid="signup-name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="mt-1.5 rounded-2xl h-11"
                    placeholder="Rajesh Kumar"
                  />
                </div>
                <div>
                  <Label htmlFor="dob">{t("signupUI.dobLabel")}</Label>
                  <Input
                    id="dob"
                    data-testid="signup-dob"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    className="mt-1.5 rounded-2xl h-11"
                    placeholder="15/08/1990"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("signupUI.genderLabel")}</Label>
                    <Select value={gender} onValueChange={setGender}>
                      <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="signup-gender">
                        <SelectValue placeholder={t("signupUI.select")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{t("signupUI.male")}</SelectItem>
                        <SelectItem value="female">{t("signupUI.female")}</SelectItem>
                        <SelectItem value="other">{t("signupUI.other")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("signupUI.bloodLabel")}</Label>
                    <Select value={bloodGroup} onValueChange={setBloodGroup}>
                      <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="signup-blood">
                        <SelectValue placeholder={t("signupUI.select")} />
                      </SelectTrigger>
                      <SelectContent>
                        {BLOOD_GROUPS.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="city">{t("signupUI.cityLabel")}</Label>
                  <Input
                    id="city"
                    data-testid="signup-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-1.5 rounded-2xl h-11"
                    placeholder="Mumbai"
                  />
                </div>
                <div className="space-y-2 pt-2">
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="signup-terms" className="mt-0.5" />
                    <span className="text-muted-foreground">{t("signupUI.acceptTerms")}</span>
                  </label>
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <Checkbox checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)} data-testid="signup-privacy" className="mt-0.5" />
                    <span className="text-muted-foreground flex items-center gap-1">
                      <ShieldCheck className="h-3.5 w-3.5" /> {t("signupUI.acceptPrivacy")}
                    </span>
                  </label>
                </div>
              </div>
              <Button
                className="w-full mt-6 h-12 rounded-2xl text-base"
                onClick={handleSignup}
                disabled={loading}
                data-testid="complete-signup-button"
              >
                {loading ? t("signupUI.creating") : t("signupUI.complete")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
